import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PUBLICATION_STATUSES, type Publication, type PublicationStatus } from '../types';
import { useUpdatePublication } from '../hooks';
import { DmsImage } from './DmsImage';
import { ChannelsPicker } from './ChannelsPicker';

interface Props {
  publication: Publication | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const INTENTIONS = ['To Inform', 'To Persuade', 'To Entertain', 'To Humanized'];

export function PublicationDetailModal({ publication, open, onOpenChange }: Props) {
  const updateMutation = useUpdatePublication();

  const [titleEn, setTitleEn] = useState('');
  const [titleFr, setTitleFr] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [bodyFr, setBodyFr] = useState('');
  const [status, setStatus] = useState<PublicationStatus>('Ideation');
  const [channels, setChannels] = useState<string[]>([]);
  const [intentions, setIntentions] = useState<string>('');
  const [publishDate, setPublishDate] = useState('');

  useEffect(() => {
    if (open && publication) {
      setTitleEn(publication.titleEn ?? '');
      setTitleFr(publication.titleFr ?? '');
      setBodyEn(publication.bodyEn ?? '');
      setBodyFr(publication.bodyFr ?? '');
      setStatus(publication.status);
      setChannels(publication.channels);
      setIntentions(publication.intentions ?? '');
      setPublishDate(publication.publishDate ? publication.publishDate.slice(0, 10) : '');
    }
  }, [open, publication]);

  if (!publication) return null;

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      id: publication.id,
      data: {
        title_en: titleEn || null,
        title_fr: titleFr || null,
        body_en: bodyEn || null,
        body_fr: bodyFr || null,
        status,
        channels,
        intentions: intentions || null,
        publish_date: publishDate ? new Date(publishDate).toISOString() : null,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(96vw,1600px)] w-[96vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Publication</DialogTitle>
          <DialogDescription>
            {publication.nocodbId != null ? `NocoDB #${publication.nocodbId}` : 'New publication'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {/* Metadata row: Status · Publish date · Intentions */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as PublicationStatus)}>
                <SelectTrigger id="status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PUBLICATION_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="publishDate">Publish date</Label>
              <Input
                id="publishDate"
                type="date"
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="intentions">Intentions</Label>
              <Select value={intentions || '__none__'} onValueChange={(v) => setIntentions(v === '__none__' ? '' : v)}>
                <SelectTrigger id="intentions"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {INTENTIONS.map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Channels: multi-select checkboxes, full width */}
          <div>
            <Label className="mb-2 block">Channels</Label>
            <ChannelsPicker value={channels} onChange={setChannels} />
          </div>

          {/* EN / FR in two columns with strictly aligned rows */}
          <div className="grid grid-cols-2 gap-x-6 items-start">
            {/* Row 1: language label */}
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 pb-2">English</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 pb-2">Français</div>

            {/* Row 2: Visual gallery (all attachments per locale) */}
            <div className="pb-4">
              {publication.visualEnDmsIds.length > 0 ? (
                <div className={`grid gap-2 ${publication.visualEnDmsIds.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {publication.visualEnDmsIds.map((id, i) => (
                    <DmsImage
                      key={id}
                      documentId={id}
                      alt={`Visual EN ${i + 1}`}
                      className="w-full max-h-[50vh] object-contain bg-slate-50 rounded border"
                    />
                  ))}
                </div>
              ) : (
                <div className="w-full h-80 flex items-center justify-center bg-slate-50 text-slate-400 text-xs rounded border">
                  No image
                </div>
              )}
            </div>
            <div className="pb-4">
              {publication.visualFrDmsIds.length > 0 ? (
                <div className={`grid gap-2 ${publication.visualFrDmsIds.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {publication.visualFrDmsIds.map((id, i) => (
                    <DmsImage
                      key={id}
                      documentId={id}
                      alt={`Visual FR ${i + 1}`}
                      className="w-full max-h-[50vh] object-contain bg-slate-50 rounded border"
                    />
                  ))}
                </div>
              ) : (
                <div className="w-full h-80 flex items-center justify-center bg-slate-50 text-slate-400 text-xs rounded border">
                  Aucune image
                </div>
              )}
            </div>

            {/* Row 3: Title */}
            <div className="pb-4">
              <Label htmlFor="titleEn">Title</Label>
              <Input id="titleEn" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} />
            </div>
            <div className="pb-4">
              <Label htmlFor="titleFr">Titre</Label>
              <Input id="titleFr" value={titleFr} onChange={(e) => setTitleFr(e.target.value)} />
            </div>

            {/* Row 4: Body */}
            <div>
              <Label htmlFor="bodyEn">Body</Label>
              <Textarea id="bodyEn" rows={12} value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="bodyFr">Corps</Label>
              <Textarea id="bodyFr" rows={12} value={bodyFr} onChange={(e) => setBodyFr(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
